import {DataTable} from "../DataTable/DataTable";
import {CustomFieldProfileEntity} from "@ots-frontend/ots-data";
import {useTranslation} from "react-i18next";
import {usePagination} from "../DataTable/usePagination";
import {getColumnFilterProps} from "../DataTable/useDataTableFilter";
import {
  useCustomFieldProfileDataTableFilter
} from "../CustomFieldProfileDataTable/useCustomFieldProfileDataTableFilter";
import {useDataTableSorter} from "../DataTable/useDataTableSorter";
import {EntityDataTableProps} from "../EntityDataTable/EntityDataTable";
import {MouseEvent, useCallback, useMemo} from "react";
import {DataTableColumn} from "mantine-datatable";
import {sortBy} from "lodash";
import {useNavigate} from "react-router-dom";
import {useCustomFieldProfileAction} from "../../menu/CustomFieldProfileActionMenu/useCustomFieldProfileAction";
import {EntityActionGroup} from "../../group/EntityActionGroup/EntityActionGroup";
import {getEntityTypeDisplayValueAsString} from "../../../util/entity-ui-util";
import { FormattedDateTime } from "../../date/FormattedDateTime/FormattedDateTime";
import { Badge } from "../../badge/Badge/Badge";

const EMPTY_ARRAY: CustomFieldProfileEntity[] = []

export type CustomFieldProfileDataTableProps = EntityDataTableProps<CustomFieldProfileEntity>

export const CustomFieldProfileDataTable = (props: CustomFieldProfileDataTableProps) => {
  const {
    records: profiles = EMPTY_ARRAY,
    recordsPerPageOptions,
    ...rest
  } = props

  const {t} = useTranslation(["common"]);
  const navigate = useNavigate()

    const {filteredRecords, filterConfig} = useCustomFieldProfileDataTableFilter({
      records: profiles
    })

    const sortConfig = useMemo(() => {
      return {
        "label": {
          sortAll: (records, columnAccessor) => {
            return sortBy(records, [function (record) {
              return record.label?.toLowerCase()
            }])
          }
        },
      }
  }, [])

  const {
    sortedRecords,
    sortStatus,
    onSortStatusChange,
  } = useDataTableSorter({
    records: filteredRecords,
    sortStatus: {columnAccessor: 'label', direction: 'asc'},
    sortConfig: sortConfig,
  })

  const pagination = usePagination({
    data: sortedRecords,
    recordsPerPageOptions: recordsPerPageOptions,
  });

  const {
    onCustomFieldProfileActionClick
  } = useCustomFieldProfileAction()

  const onViewActionClick = useCallback((e: MouseEvent<HTMLButtonElement>, record: CustomFieldProfileEntity) => {
    e.stopPropagation()
    navigate(`/custom-field-profiles/profile?id=${record.id}`)
  }, [navigate])

  const onEditActionClick = useCallback((e: MouseEvent<HTMLButtonElement>, record: CustomFieldProfileEntity) => {
    e.stopPropagation()
    onCustomFieldProfileActionClick(record, 'EDIT')
  }, [onCustomFieldProfileActionClick])

  const onDeleteActionClick = useCallback((e: MouseEvent<HTMLButtonElement>, record: CustomFieldProfileEntity) => {
    e.stopPropagation()
    onCustomFieldProfileActionClick(record, 'DELETE')
  }, [onCustomFieldProfileActionClick])

  const columns = useMemo(() => {
    const values: DataTableColumn<CustomFieldProfileEntity>[] = [
      {
        ...getColumnFilterProps(filterConfig, "name"),
        title: t("common.entity.customfieldprofile.table.column.name", "Name"),
        accessor: 'name',
        width: 100,
        sortable: true,
      },
      {
        ...getColumnFilterProps(filterConfig, "companyName"),
        title: t("common.entity.customfieldprofile.table.column.company", "Company"),
        accessor: 'companyName',
        width: 100,
        sortable: true,
      },
      {
        ...getColumnFilterProps(filterConfig, "forObjectType"),
        title: t("common.entity.customfieldprofile.table.column.forobject", "Object Type"),
        accessor: 'forObjectType',
        width: 100,
        sortable: true,
        render: ({forObjectType}) => {
          return getEntityTypeDisplayValueAsString(forObjectType, t)
        }
      },
      {
        title: t("common.entity.customfieldprofile.table.column.effectivefrom", "Effective From"),
        accessor: 'effectiveFromDttm',
        width: 100,
        sortable: true,
        render: ({effectiveFromDttm}) => {
          return <FormattedDateTime date={effectiveFromDttm}/>
        },
      },
      {
        title: t("common.entity.customfieldprofile.table.column.effectiveto", "Effective To"),
        accessor: 'effectiveToDttm',
        width: 100,
        sortable: true,
        render: ({effectiveToDttm}) => {
          return effectiveToDttm ? <FormattedDateTime date={effectiveToDttm}/> : undefined
        },
      },
      {
        title: 'Active',
        accessor: "active",
        width: 35,
        render: (profile) => {
          if (profile.isActive()) {
            return <Badge color="green">{t("common.status.active", "Active")}</Badge>
          }

          return undefined
        }
      },
      {
        title: '',
        accessor: "actions",
        width: 100,
        textAlignment: 'right',
        render: (profile) => (
            <EntityActionGroup
                permissionPrefix="customfieldprofile"
                view={false}
                onViewActionClick={(e) => onViewActionClick(e, profile)}
                onEditActionClick={(e) => onEditActionClick(e, profile)}
                onDeleteActionClick={(e) => onDeleteActionClick(e, profile)}/>
        )
      }
    ]

    return values
  }, [filterConfig, onDeleteActionClick, onEditActionClick, onViewActionClick, t])

  return (
      <DataTable
          {...rest}
          {...(pagination ?? {})}
          sortStatus={sortStatus}
          onSortStatusChange={onSortStatusChange}
          columns={columns}
      />
  )
}
